/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Applogo } from "../../../Routes/ImagePath";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  useVerifyEmployeeMutation,
  useRegisterEmployeeMutation,
} from "../../../services/auth.service";
import alertify from "alertifyjs";

const verifySchema = Yup.object().shape({
  emp_code: Yup.string().required("Employee ID is required"),
});

const Register = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: verify, 2: choose method, 3: success
  const [employeeData, setEmployeeData] = useState(null);
  const [empCode, setEmpCode] = useState("");
  const [notificationMethod, setNotificationMethod] = useState(null); // 'email' or 'sms'

  const [verifyEmployee, { isLoading: isVerifying }] = useVerifyEmployeeMutation();
  const [registerEmployee, { isLoading: isRegistering }] = useRegisterEmployeeMutation();

  const verifyForm = useForm({
    resolver: yupResolver(verifySchema),
  });

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneInput, setPhoneInput] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const onVerifySubmit = async (data) => {
    try {
      const response = await verifyEmployee(data).unwrap();
      if (response.success) {
        setEmployeeData(response.data);
        setEmpCode(data.emp_code);
        // Pre-select method based on availability
        if (response.data.available_methods?.email && !response.data.available_methods?.sms) {
          setNotificationMethod('email');
        } else if (response.data.available_methods?.sms && !response.data.available_methods?.email) {
          setNotificationMethod('sms');
        }
        setStep(2);
      } else {
        alertify.error(response.message);
      }
    } catch (error) {
      alertify.error(error?.data?.message || "Verification failed");
    }
  };

  const validateEmail = (value) => {
    if (!value) {
      setEmailError("Email is required");
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePhone = (value) => {
    if (!value) {
      setPhoneError("Phone number is required");
      return false;
    }
    // Remove spaces, dashes, and other non-numeric characters
    const cleaned = value.replace(/[^\d]/g, '');
    // Check if it's a valid Nigerian phone number (10-11 digits starting with 0 or 11-13 digits starting with 234)
    if (cleaned.length < 10 || cleaned.length > 13) {
      setPhoneError("Please enter a valid phone number");
      return false;
    }
    setPhoneError("");
    return true;
  };

  const onRegisterSubmit = async (e) => {
    e.preventDefault();

    if (!notificationMethod) {
      alertify.error("Please select a notification method");
      return;
    }

    if (!validateEmail(email)) {
      return;
    }

    if (notificationMethod === 'sms' && !validatePhone(phoneInput)) {
      return;
    }

    try {
      const payload = {
        emp_code: empCode,
        notification_method: notificationMethod,
      };

      if (notificationMethod === 'email') {
        payload.email = email;
      }

      if (notificationMethod === 'sms') {
        payload.phone = phoneInput;
      }

      const response = await registerEmployee(payload).unwrap();

      if (response.success) {
        setStep(3);
        alertify.success(response.message);
      } else {
        alertify.error(response.message);
      }
    } catch (error) {
      alertify.error(error?.data?.message || "Registration failed");
    }
  };

  const goBack = () => {
    setStep(1);
    setEmployeeData(null);
    setEmpCode("");
    setNotificationMethod(null);
    setEmail("");
    setEmailError("");
    setPhoneInput("");
    setPhoneError("");
    verifyForm.reset();
  };

  return (
    <div>
      <div className="account-page">
        <div className="main-wrapper">
          <div className="account-content">
            <div className="container">
              {/* Account Logo */}
              <div className="account-logo">
                <Link to="/">
                  <img src={Applogo} alt="Company Logo" />
                </Link>
              </div>
              {/* /Account Logo */}
              <div className="account-box">
                <div className="account-wrapper">
                  {step === 1 && (
                    <>
                      <h3 className="account-title">Create Account</h3>
                      <p className="account-subtitle">
                        Enter your Employee ID to get started
                      </p>
                      <form onSubmit={verifyForm.handleSubmit(onVerifySubmit)}>
                        <div className="input-block mb-4">
                          <label className="col-form-label">Employee ID</label>
                          <Controller
                            name="emp_code"
                            control={verifyForm.control}
                            render={({ field }) => (
                              <input
                                className={`form-control ${
                                  verifyForm.formState.errors?.emp_code
                                    ? "error-input"
                                    : ""
                                }`}
                                type="text"
                                placeholder="Enter your Employee ID"
                                onChange={field.onChange}
                                value={field.value || ""}
                              />
                            )}
                          />
                          <span className="text-danger">
                            {verifyForm.formState.errors.emp_code?.message}
                          </span>
                        </div>
                        <div className="input-block text-center">
                          <button
                            className="btn btn-primary account-btn"
                            type="submit"
                            disabled={isVerifying}
                          >
                            {isVerifying ? "Verifying..." : "Verify"}
                          </button>
                        </div>
                      </form>
                    </>
                  )}

                  {step === 2 && employeeData && (
                    <>
                      <h3 className="account-title">Welcome!</h3>
                      <p className="account-subtitle">
                        Please verify your details and choose how to receive your temporary password
                      </p>

                      {/* Employee Details Card */}
                      <div className="card mb-4">
                        <div className="card-body">
                          <h5 className="card-title mb-3">
                            {employeeData.first_name} {employeeData.last_name}
                          </h5>
                          <p className="mb-1">
                            <strong>Employee ID:</strong> {employeeData.employee_number || empCode}
                          </p>
                          {employeeData.department?.name && (
                            <p className="mb-1">
                              <strong>Department:</strong> {employeeData.department.name}
                            </p>
                          )}
                          {employeeData.designation?.name && (
                            <p className="mb-1">
                              <strong>Designation:</strong> {employeeData.designation.name}
                            </p>
                          )}
                          {employeeData.branch?.name && (
                            <p className="mb-2">
                              <strong>Branch:</strong> {employeeData.branch.name}
                            </p>
                          )}
                          <hr />
                          {/* Contact Info (Masked) */}
                          <p className="mb-1 text-muted">
                            <strong>Email:</strong> {employeeData.masked_email || 'N/A'}
                          </p>
                          <p className="mb-0 text-muted">
                            <strong>Phone:</strong> {employeeData.masked_phone || 'N/A'}
                          </p>
                        </div>
                      </div>

                      <form onSubmit={onRegisterSubmit}>
                        {/* Notification Method Selection */}
                        <div className="input-block mb-4">
                          <label className="col-form-label">
                            How would you like to receive your temporary password?
                          </label>

                          <div className="row mt-2">
                            {/* Email Option */}
                            {employeeData.available_methods?.email && (
                              <div className="col-6">
                                <div
                                  className={`card cursor-pointer ${
                                    notificationMethod === 'email' ? 'border-primary bg-light' : ''
                                  }`}
                                  onClick={() => setNotificationMethod('email')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className="card-body text-center py-3">
                                    <i className="fa fa-envelope fa-2x mb-2 text-primary"></i>
                                    <p className="mb-0 fw-bold">Email</p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* SMS Option */}
                            {employeeData.available_methods?.sms && (
                              <div className="col-6">
                                <div
                                  className={`card cursor-pointer ${
                                    notificationMethod === 'sms' ? 'border-primary bg-light' : ''
                                  }`}
                                  onClick={() => setNotificationMethod('sms')}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className="card-body text-center py-3">
                                    <i className="fa fa-mobile fa-2x mb-2 text-primary"></i>
                                    <p className="mb-0 fw-bold">SMS</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Show message if only one method available */}
                          {!employeeData.available_methods?.email && employeeData.available_methods?.sms && (
                            <small className="text-muted d-block mt-2">
                              Email not available. SMS will be used.
                            </small>
                          )}
                          {employeeData.available_methods?.email && !employeeData.available_methods?.sms && (
                            <small className="text-muted d-block mt-2">
                              Phone not available. Email will be used.
                            </small>
                          )}
                          {!employeeData.available_methods?.email && !employeeData.available_methods?.sms && (
                            <div className="alert alert-danger mt-2">
                              No contact information on file. Please contact HR to update your records.
                            </div>
                          )}
                        </div>

                        {/* Email Input (only shown when email method selected) */}
                        
                          <div className="input-block mb-4">
                            <label className="col-form-label">Email Address</label>
                            <input
                              className={`form-control ${emailError ? "error-input" : ""}`}
                              type="email"
                              placeholder="Enter your email address"
                              value={email}
                              onChange={(e) => {
                                setEmail(e.target.value);
                                if (emailError) validateEmail(e.target.value);
                              }}
                            />
                            {emailError && (
                              <span className="text-danger">{emailError}</span>
                            )}
                            <small className="text-muted d-block mt-1">
                              A temporary password will be sent to this email
                            </small>
                          </div>
                        

                        {/* Phone Input (only shown when SMS method selected) */}
                        {notificationMethod === 'sms' && (
                          <div className="input-block mb-4">
                            <label className="col-form-label">Phone Number</label>
                            <input
                              className={`form-control ${phoneError ? "error-input" : ""}`}
                              type="tel"
                              placeholder="Enter your phone number (e.g., 08012345678)"
                              value={phoneInput}
                              onChange={(e) => {
                                setPhoneInput(e.target.value);
                                if (phoneError) validatePhone(e.target.value);
                              }}
                            />
                            {phoneError && (
                              <span className="text-danger">{phoneError}</span>
                            )}
                            <small className="text-muted d-block mt-1">
                              Enter the phone number ending in <strong>{employeeData.masked_phone?.slice(-4) || '****'}</strong> to verify your identity
                            </small>
                          </div>
                        )}

                        <div className="input-block text-center">
                          <button
                            className="btn btn-primary account-btn"
                            type="submit"
                            disabled={
                              isRegistering ||
                              !notificationMethod ||
                              (!employeeData.available_methods?.email && !employeeData.available_methods?.sms)
                            }
                          >
                            {isRegistering ? "Creating Account..." : "Create Account"}
                          </button>
                        </div>
                        <div className="text-center mt-3">
                          <button
                            type="button"
                            className="btn btn-link"
                            onClick={goBack}
                          >
                            Back
                          </button>
                        </div>
                      </form>
                    </>
                  )}

                  {step === 3 && (
                    <>
                      <div className="text-center">
                        <div className="mb-4">
                          <i
                            className="fa fa-check-circle text-success"
                            style={{ fontSize: "64px" }}
                          ></i>
                        </div>
                        <h3 className="account-title">Account Created!</h3>
                        <p className="account-subtitle">
                          {notificationMethod === 'email' ? (
                            <>
                              A temporary password has been sent to your email address.
                              Please check your inbox and use it to log in.
                            </>
                          ) : (
                            <>
                              A temporary password has been sent to your phone via SMS.
                              Please check your messages and use it to log in.
                            </>
                          )}
                        </p>
                        <p className="text-muted mb-4">
                          You will be required to change your password on first login.
                        </p>
                        <Link to="/" className="btn btn-primary account-btn">
                          Go to Login
                        </Link>
                      </div>
                    </>
                  )}

                  {step !== 3 && (
                    <div className="account-footer">
                      <p>
                        Already have an account? <Link to="/">Login</Link>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
